import { useEffect, useState, useCallback } from "react";
import { api } from "@/lib/api";
import VideoCard from "@/components/VideoCard";
import { usePlayer } from "@/contexts/PlayerContext";
import { useAuth } from "@/contexts/AuthContext";
import { TEST_IDS } from "@/constants/testIds";
import { MagnifyingGlass } from "@phosphor-icons/react";
import { toast } from "sonner";

const PRESETS = [
  { label: "Afrobeats", q: "afrobeats music" },
  { label: "Latin", q: "latin music hits" },
  { label: "K-Pop", q: "kpop music" },
  { label: "Jazz", q: "world jazz" },
  { label: "Reggae", q: "reggae music" },
  { label: "Electronic", q: "world electronic music" },
  { label: "Bollywood", q: "bollywood songs" },
  { label: "Arabic", q: "arabic music" },
];

export default function Videos() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [favs, setFavs] = useState(new Set());
  const [playlists, setPlaylists] = useState([]);
  const [pickerVideo, setPickerVideo] = useState(null);

  const { playVideo } = usePlayer();
  const { user } = useAuth();

  const loadFavs = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/favorites/videos");
      setFavs(new Set(data.map(f => f.video_id)));
    } catch (_) { /* ignore */ }
  }, [user]);

  const loadPlaylists = useCallback(async () => {
    if (!user) return;
    try {
      const { data } = await api.get("/playlists");
      setPlaylists(data || []);
    } catch (_) { /* ignore */ }
  }, [user]);

  const loadTrending = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/youtube/trending", { params: { region: "US", max_results: 24 } });
      setItems(data.items || []);
      setUnavailable(false);
    } catch (e) {
      if (e.response?.status === 503) {
        setUnavailable(true);
      } else {
        toast.error("Failed to load trending videos");
      }
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTrending(); }, [loadTrending]);
  useEffect(() => { loadFavs(); loadPlaylists(); }, [loadFavs, loadPlaylists]);

  const doSearch = async (query) => {
    if (!query.trim()) return;
    setLoading(true);
    try {
      const { data } = await api.get("/youtube/search", { params: { q: query, max_results: 24 } });
      setItems(data.items || []);
      setUnavailable(false);
    } catch (e) {
      if (e.response?.status === 429) toast.error("YouTube quota exceeded — try again later");
      else if (e.response?.status === 503) setUnavailable(true);
      else toast.error("Search failed");
    } finally { setLoading(false); }
  };

  const onFav = async (video) => {
    if (!user) { toast.error("Please sign in to save favorites"); return; }
    try {
      if (favs.has(video.video_id)) {
        await api.delete(`/favorites/videos/${video.video_id}`);
        setFavs(prev => { const s = new Set(prev); s.delete(video.video_id); return s; });
      } else {
        await api.post("/favorites/videos", video);
        setFavs(prev => new Set(prev).add(video.video_id));
      }
    } catch (_) { toast.error("Action failed"); }
  };

  const onAddToPlaylist = (video) => {
    if (!user) { toast.error("Please sign in to use playlists"); return; }
    setPickerVideo(video);
  };

  const addToPlaylist = async (pl) => {
    try {
      await api.post(`/playlists/${pl.id}/videos`, pickerVideo);
      toast.success(`Added to "${pl.name}"`);
      setPickerVideo(null);
    } catch (_) { toast.error("Failed to add"); }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-12 pb-32">
      <div className="mb-10 border-b border-[#1F2924]/20 pb-6">
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#D35230] mb-3">Cinema of Sound</div>
        <h1 className="font-serif-display text-5xl sm:text-6xl leading-tight">Music videos</h1>
        <p className="text-[#1F2924]/70 mt-3 max-w-2xl">Discover trending music from across the world via YouTube.</p>
      </div>

      <form
        onSubmit={(e) => { e.preventDefault(); doSearch(q); }}
        className="mb-6 flex items-center gap-3"
      >
        <div className="relative flex-1">
          <MagnifyingGlass size={18} className="absolute left-0 top-1/2 -translate-y-1/2" />
          <input
            data-testid={TEST_IDS.video.searchInput}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search artists, songs, genres…"
            className="w-full pl-7 pr-3 py-2.5 bg-transparent border-b-2 border-[#1F2924]/30 focus:border-[#D35230] outline-none font-sans"
          />
        </div>
        <button
          data-testid={TEST_IDS.video.searchBtn}
          type="submit"
          className="px-5 py-2.5 border-2 border-[#1F2924] hover:bg-[#1F2924] hover:text-[#F4F1EA] font-mono text-xs uppercase tracking-[0.2em]"
        >
          Search
        </button>
      </form>

      <div className="flex flex-wrap gap-2 mb-10">
        {PRESETS.map(p => (
          <button
            key={p.label}
            onClick={() => { setQ(p.q); doSearch(p.q); }}
            className="px-3 py-1.5 border border-[#1F2924]/40 hover:border-[#D35230] hover:text-[#D35230] font-mono text-[10px] uppercase tracking-[0.2em]"
          >
            {p.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#D35230]">Loading…</div>
      ) : unavailable ? (
        <div className="text-center py-16 border-2 border-dashed border-[#1F2924]/20">
          <div className="font-mono text-xs uppercase tracking-[0.3em] text-[#D35230] mb-4">YouTube Unavailable</div>
          <p className="text-[#1F2924]/70 max-w-md mx-auto">
            YouTube video discovery is temporarily unavailable. You can still play videos from your favorites or playlists.
          </p>
        </div>
      ) : (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
          {items.map(v => (
            <VideoCard
              key={v.video_id}
              video={v}
              onPlay={playVideo}
              onFav={onFav}
              isFav={favs.has(v.video_id)}
              onAddToPlaylist={onAddToPlaylist}
            />
          ))}
        </div>
      )}

      {pickerVideo && (
        <div className="fixed inset-0 bg-[#1F2924]/70 z-50 flex items-center justify-center p-4" onClick={() => setPickerVideo(null)}>
          <div className="bg-[#F4F1EA] border-2 border-[#1F2924] shadow-tactile p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#D35230] mb-2">Add to playlist</div>
            <h3 className="font-serif-display text-2xl mb-4 line-clamp-2">{pickerVideo.title}</h3>
            {playlists.length === 0 ? (
              <p className="font-sans text-sm text-[#4A5A50]">No playlists yet. Create one from your Library.</p>
            ) : (
              <div className="space-y-2 max-h-64 overflow-auto">
                {playlists.map(pl => (
                  <button
                    key={pl.id}
                    onClick={() => addToPlaylist(pl)}
                    className="w-full text-left px-3 py-2 border border-[#1F2924]/30 hover:border-[#1F2924] hover:bg-[#EAE4D3] font-sans"
                  >
                    {pl.name} <span className="font-mono text-xs text-[#4A5A50]">· {pl.video_ids?.length || 0}</span>
                  </button>
                ))}
              </div>
            )}
            <div className="mt-4 flex justify-end">
              <button onClick={() => setPickerVideo(null)} className="px-4 py-2 border-2 border-[#1F2924] font-mono text-xs uppercase tracking-[0.2em]">Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
